// PermissionContext.tsx
import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useMachine } from '@xstate/react';
import { StateFrom } from 'xstate';
import { User, usersFSM, EventType, Event } from '../fsm/users';
import { readSessionClaims } from '../utils/session';

type MachineState = StateFrom<typeof usersFSM>;

interface AccountContextType {
  user?: User;
  state: MachineState;
  send: (event: Event) => void;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const AccountProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [state, send] = useMachine(usersFSM);

  useEffect(() => {
    // jwtDecode throws on a malformed token, and with no error boundary in
    // the tree that unmounted the whole app -- a white page with no way back
    // but clearing site data. A bad token now reads as no token.
    const decoded = readSessionClaims<{ userId: number }>();
    const { userId } = decoded || {
      userId: 0,
    };
    if (userId) {
      send({
        type: EventType.GET_USER,
        payload: { id: userId },
      });
    }
  }, [send]);

  return (
    <AccountContext.Provider value={{ user: state.context.user, state, send }}>
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = (): AccountContextType => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount error');
  }
  return context;
};
